/* Complete The Look — Carousel + Variant Picker + Add To Cart */
(function () {
  'use strict';

  class CompleteTheLook {
    constructor(section) {
      this.section = section;
      this.track = section.querySelector('.ctl-track');
      this.cards = [...section.querySelectorAll('.ctl-card')];
      this.prevBtn = section.querySelector('.ctl-nav-prev');
      this.nextBtn = section.querySelector('.ctl-nav-next');
      this.counter = section.querySelector('.ctl-counter');
      this.currentIndex = 0;

      if (!this.track || !this.cards.length) return;

      this.init();
    }

    get isMobile() { return window.innerWidth < 768; }

    get visibleCount() {
      if (window.innerWidth >= 1025) return 3;
      if (window.innerWidth >= 768) return 2;
      return 1;
    }

    get maxIndex() {
      return Math.max(0, this.cards.length - this.visibleCount);
    }

    get totalPages() {
      return Math.ceil(this.cards.length / this.visibleCount);
    }

    get currentPage() {
      return Math.min(Math.ceil((this.currentIndex + 1) / this.visibleCount), this.totalPages);
    }

    init() {
      this.setupNavigation();
      this.setupVariantSelectors();
      this.setupAddToCart();
      this.updateUI();

      let resizeTimer;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          this.currentIndex = 0;
          this.updateUI();
        }, 150);
      });
    }

    /* ─── Navigation ─────────────────────────── */

    setupNavigation() {
      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => {
          this.currentIndex = Math.max(0, this.currentIndex - 1);
          this.updateUI();
        });
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => {
          this.currentIndex = Math.min(this.maxIndex, this.currentIndex + 1);
          this.updateUI();
        });
      }
    }

    updateUI() {
      if (this.isMobile) {
        this.track.style.transform = '';
        this.updateNavState();
        return;
      }

      const firstCard = this.cards[0];
      if (!firstCard) return;
      const gap = parseFloat(getComputedStyle(this.track).gap) || 24;
      const cardWidth = firstCard.offsetWidth + gap;
      this.track.style.transform = `translateX(-${this.currentIndex * cardWidth}px)`;

      if (this.counter) {
        this.counter.textContent = `${this.currentPage} / ${this.totalPages}`;
      }

      this.updateNavState();
    }

    updateNavState() {
      if (this.prevBtn) this.prevBtn.disabled = this.currentIndex === 0;
      if (this.nextBtn) this.nextBtn.disabled = this.currentIndex >= this.maxIndex;
    }

    /* ─── Variant Selection ──────────────────── */

    setupVariantSelectors() {
      this.cards.forEach(card => {
        const selects = [...card.querySelectorAll('.ctl-select')];
        const hiddenColor = card.querySelector('.ctl-hidden-color');
        const variantsEl = card.querySelector('.ctl-variants-data');
        const atcBtn = card.querySelector('.ctl-atc-btn');
        const priceEl = card.querySelector('.ctl-card__price');

        if (!variantsEl) return;

        let variants;
        try { variants = JSON.parse(variantsEl.textContent.trim()); }
        catch (e) { return; }

        const getOptionKey = (idx) => `option${parseInt(idx) + 1}`;

        const buildOptionMap = () => {
          const opts = {};
          selects.forEach(sel => { opts[sel.dataset.optionIndex] = sel.value; });
          if (hiddenColor) opts[hiddenColor.dataset.optionIndex] = hiddenColor.value;
          return opts;
        };

        const allSelectsFilled = () => selects.every(sel => sel.value !== '');

        const findVariant = () => {
          const opts = buildOptionMap();
          return variants.find(v =>
            Object.entries(opts).every(([idx, val]) => {
              if (!val) return true;
              return v[getOptionKey(idx)] === val;
            })
          );
        };

        const updateCard = () => {
          const variant = findVariant();

          if (priceEl && variant) {
            let html = '';
            if (variant.compare_at_price && variant.compare_at_price > variant.price) {
              html += `<s class="ctl-price-compare">${this.formatMoney(variant.compare_at_price)}</s>`;
            }
            html += `<span class="ctl-price-current">${this.formatMoney(variant.price)}</span>`;
            priceEl.innerHTML = html;
          }

          if (!atcBtn) return;
          const textEl = atcBtn.querySelector('.ctl-atc-btn__text');

          if (selects.length > 0 && !allSelectsFilled()) {
            atcBtn.disabled = true;
            atcBtn.dataset.variantId = '';
            if (textEl) textEl.textContent = 'SELECT SIZE';
          } else if (!variant || !variant.available) {
            atcBtn.disabled = true;
            atcBtn.dataset.variantId = '';
            if (textEl) textEl.textContent = 'SOLD OUT';
          } else {
            atcBtn.disabled = false;
            atcBtn.dataset.variantId = variant.id;
            if (textEl) textEl.textContent = 'ADD TO BAG';
          }
        };

        selects.forEach(sel => sel.addEventListener('change', updateCard));

        if (selects.length > 0 && atcBtn) {
          atcBtn.disabled = true;
          const textEl = atcBtn.querySelector('.ctl-atc-btn__text');
          if (textEl) textEl.textContent = 'SELECT SIZE';
        }
      });
    }

    /* ─── Add To Cart ────────────────────────── */

    setupAddToCart() {
      this.cards.forEach(card => {
        const btn = card.querySelector('.ctl-atc-btn');
        if (!btn) return;

        btn.addEventListener('click', async () => {
          const variantId = btn.dataset.variantId;
          if (!variantId || btn.disabled) return;

          const textEl = btn.querySelector('.ctl-atc-btn__text');
          const origText = textEl ? textEl.textContent : 'ADD TO BAG';

          btn.classList.add('ctl-loading');

          try {
            const res = await fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
              body: JSON.stringify({ id: parseInt(variantId), quantity: 1 })
            });

            if (!res.ok) throw new Error('Cart add failed');

            btn.classList.remove('ctl-loading');
            btn.classList.add('ctl-added');
            if (textEl) textEl.textContent = 'ADDED!';

            this.refreshCart();

            setTimeout(() => {
              btn.classList.remove('ctl-added');
              if (textEl) textEl.textContent = origText;
            }, 2200);

          } catch (err) {
            btn.classList.remove('ctl-loading');
            console.error('[CTL] Add to cart failed:', err);
          }
        });
      });
    }

    refreshCart() {
      const cartDrawer = document.querySelector('m-cart-drawer');

      // Update cart count badge
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      fetch(root + 'cart.js')
        .then(r => r.json())
        .then(cart => {
          document.querySelectorAll('.m-cart-count-bubble').forEach(el => {
            if (cart.item_count > 0) {
              el.textContent = cart.item_count;
              el.classList.remove('m:hidden');
            } else {
              el.classList.add('m:hidden');
            }
          });
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart }, bubbles: true }));
        })
        .catch(() => {});

      // Open and refresh the Minimog slide cart drawer
      if (cartDrawer && window.MinimogSettings && window.MinimogSettings.enable_cart_drawer) {
        cartDrawer.onCartDrawerUpdate();
        setTimeout(() => {
          cartDrawer.open();
          if (window.MinimogEvents && window.MinimogTheme && window.MinimogTheme.pubSubEvents) {
            window.MinimogEvents.emit(window.MinimogTheme.pubSubEvents.openCartDrawer);
          }
        }, 300);
      }
    }

    /* ─── Money Formatter ────────────────────── */

    formatMoney(cents) {
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        const fmt = this.section.dataset.moneyFormat
          || (window.theme && window.theme.moneyFormat)
          || '{{amount}}';
        return window.Shopify.formatMoney(cents, fmt);
      }
      const amount = Math.round(cents / 100);
      return '₹' + amount.toLocaleString('en-IN');
    }
  }

  function initAll() {
    document.querySelectorAll('[id^="ctl-section-"]').forEach(el => new CompleteTheLook(el));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
